/**
 * ProfileTab - Company profile editing form.
 * Per CONTEXT.md: Explicit save button, validation on submit only.
 */

import { useTranslation } from 'react-i18next';
import type { CompanyProfile } from '../../types/company';
import LogoUpload from './LogoUpload';
import PhoneInput from '../base/PhoneInput';

interface ProfileTabProps {
  profile: CompanyProfile;
  formData: Partial<CompanyProfile>;
  onChange: (data: Partial<CompanyProfile>) => void;
  onSave: () => void;
  onLogoUpload: (file: File) => void;
  onLogoRemove: () => void;
  isSaving: boolean;
  isUploadingLogo: boolean;
  errors: Record<string, string>;
  canEdit: boolean;
}

export default function ProfileTab({
  profile,
  formData,
  onChange,
  onSave,
  onLogoUpload,
  onLogoRemove,
  isSaving,
  isUploadingLogo,
  errors,
  canEdit,
}: ProfileTabProps) {
  const { t } = useTranslation('companySettings');

  const handleInputChange = (field: keyof CompanyProfile) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange({ ...formData, [field]: e.target.value });
  };

  return (
    <div className="space-y-8">
      {/* Section header */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--pv-color-primary)]">{t('profile.heading')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('profile.description')}</p>
      </div>

      {/* Logo upload */}
      <LogoUpload
        currentLogoUrl={profile.logoUrl || null}
        onUploadComplete={() => {}}
        onRemove={onLogoRemove}
        onCropRequest={onLogoUpload}
        isUploading={isUploadingLogo}
        disabled={!canEdit}
      />

      {/* Form fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company Name (English) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.name')}
          </label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={handleInputChange('name')}
            placeholder={t('profile.placeholders.name')}
            disabled={!canEdit}
            className={`
              w-full px-3 py-2 border rounded-lg text-sm
              ${errors.name ? 'border-red-500' : 'border-gray-300'}
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Company Name (Arabic) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.nameAr')}
          </label>
          <input
            type="text"
            value={formData.nameAr || ''}
            onChange={handleInputChange('nameAr')}
            placeholder={t('profile.placeholders.nameAr')}
            disabled={!canEdit}
            dir="rtl"
            className={`
              w-full px-3 py-2 border rounded-lg text-sm border-gray-300
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.email')}
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={handleInputChange('email')}
            placeholder={t('profile.placeholders.email')}
            disabled={!canEdit}
            className={`
              w-full px-3 py-2 border rounded-lg text-sm
              ${errors.email ? 'border-red-500' : 'border-gray-300'}
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.phone')}
          </label>
          <PhoneInput
            value={formData.phone || ''}
            onChange={(value) => onChange({ ...formData, phone: value })}
            placeholder={t('profile.placeholders.phone')}
            disabled={!canEdit}
          />
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.address')}
          </label>
          <input
            type="text"
            value={formData.address || ''}
            onChange={handleInputChange('address')}
            placeholder={t('profile.placeholders.address')}
            disabled={!canEdit}
            className={`
              w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
        </div>

        {/* Website */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.website')}
          </label>
          <input
            type="url"
            value={formData.website || ''}
            onChange={handleInputChange('website')}
            placeholder={t('profile.placeholders.website')}
            disabled={!canEdit}
            className={`
              w-full px-3 py-2 border rounded-lg text-sm
              ${errors.website ? 'border-red-500' : 'border-gray-300'}
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
          {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
        </div>

        {/* Description (English) */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.description')}
          </label>
          <textarea
            value={formData.description || ''}
            onChange={handleInputChange('description')}
            placeholder={t('profile.placeholders.description')}
            disabled={!canEdit}
            rows={3}
            className={`
              w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
        </div>

        {/* Description (Arabic) */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.fields.descriptionAr')}
          </label>
          <textarea
            value={formData.descriptionAr || ''}
            onChange={handleInputChange('descriptionAr')}
            placeholder={t('profile.placeholders.descriptionAr')}
            disabled={!canEdit}
            rows={3}
            dir="rtl"
            className={`
              w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
              ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#004D40] focus:border-transparent'}
            `}
          />
        </div>
      </div>

      {/* Save button */}
      {canEdit && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-6 py-2 bg-[#004D40] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? t('profile.saving') : t('profile.save')}
          </button>
        </div>
      )}
    </div>
  );
}
